
import React, { useState, useRef } from 'react';
import { Wine, WineType } from '../types';
import { extractWineInfoFromImages } from '../services/geminiService';
import { compressImage } from '../utils/imageUtils';

interface PendingBottle {
  id: string;
  front: string | null;
  back: string | null;
  status: 'pending' | 'loading' | 'done' | 'error';
  error?: string;
}

interface AddWineModalProps {
  onAdd: (wines: Wine[]) => void;
  onClose: () => void;
}

const AddWineModal: React.FC<AddWineModalProps> = ({ onAdd, onClose }) => {
  const [pendingBottles, setPendingBottles] = useState<PendingBottle[]>([
    { id: Math.random().toString(36).substr(2, 9), front: null, back: null, status: 'pending' }
  ]);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeSlotRef = useRef<{ bottleId: string, side: 'front' | 'back' } | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const activeSlot = activeSlotRef.current;
    if (!file || !activeSlot) return;

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result as string;
        const optimized = await compressImage(base64, 1000, 1000, 0.75);
        
        setPendingBottles(prev => prev.map(b => {
          if (b.id === activeSlot.bottleId) {
            return { ...b, [activeSlot.side]: optimized, status: 'pending', error: undefined };
          }
          return b;
        }));
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error("Errore caricamento immagine:", err);
    }
    e.target.value = '';
  };

  const triggerPicker = (bottleId: string, side: 'front' | 'back') => {
    activeSlotRef.current = { bottleId, side };
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 10);
  };

  const removeBottle = (id: string) => {
    if (pendingBottles.length === 1) {
      // Se è l'ultima, resettala invece di eliminarla
      setPendingBottles([{ id: Math.random().toString(36).substr(2, 9), front: null, back: null, status: 'pending' }]);
    } else {
      setPendingBottles(prev => prev.filter(b => b.id !== id));
    }
  };

  const processAll = async () => {
    setIsProcessing(true);
    const bottlesToProcess = pendingBottles.filter(b => (b.front || b.back) && b.status !== 'done');
    const newWines: Wine[] = [];
    
    for (const bottle of bottlesToProcess) {
      setPendingBottles(prev => prev.map(p => p.id === bottle.id ? { ...p, status: 'loading' } : p));
      
      const images = [bottle.front, bottle.back].filter(Boolean) as string[];
      
      try {
        const { wine: extracted } = await extractWineInfoFromImages(images);
        
        newWines.push({
          id: Math.random().toString(36).substr(2, 9),
          name: extracted.name || "Vino Sconosciuto",
          producer: extracted.producer || "Produttore Ignoto",
          vintage: extracted.vintage || "S.A.",
          type: (extracted.type as WineType) || WineType.OTHER,
          price: Number(extracted.price) || 0,
          description: extracted.description || "",
          grapes: extracted.grapes || "",
          foodPairings: Array.isArray(extracted.foodPairings) ? extracted.foodPairings : [],
          drinkingWindow: extracted.drinkingWindow || "Pronto",
          imageUrls: extracted.imageUrls || [],
          quantity: 1,
          addedAt: Date.now()
        });
        
        setPendingBottles(prev => prev.map(p => p.id === bottle.id ? { ...p, status: 'done' } : p));
      } catch (err: any) {
        console.error("Dettaglio Errore Gemini:", err);
        setPendingBottles(prev => prev.map(p => p.id === bottle.id ? { ...p, status: 'error', error: err.message } : p));
      }
    }

    if (newWines.length > 0) {
      onAdd(newWines);
    }
    setIsProcessing(false);
  };

  return (
    <div className="fixed inset-0 bg-[#0a0503] z-[200] flex flex-col overflow-hidden">
      {/* Header Fisso con Safe Area */}
      <div className="flex justify-between items-center px-6 pt-[calc(env(safe-area-inset-top)+0.5rem)] pb-4 bg-black/40 backdrop-blur-2xl border-b border-white/5 z-[210]">
        <h2 className="text-xl font-bold text-amber-500 tracking-tight serif">Inserimento Cantina</h2>
        <button 
          onClick={onClose} 
          className="bg-white/10 p-2 rounded-full text-white active:scale-90 transition-transform" 
          disabled={isProcessing}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
      </div>

      {/* Area Centrale Scrollabile */}
      <div className="flex-1 overflow-y-auto ios-scroll-container p-6 space-y-10">
        {pendingBottles.map((bottle) => (
          <div key={bottle.id} className="bg-white/[0.03] p-6 rounded-[2.5rem] border border-white/10 relative shadow-2xl animate-scale-in">
            {/* Pulsante elimina slot */}
            <button 
              onClick={() => removeBottle(bottle.id)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-red-900 text-white rounded-full flex items-center justify-center shadow-lg active:scale-75 transition-transform z-20"
              disabled={isProcessing}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M3 6h18m-2 0v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
            </button>

            <div className="grid grid-cols-2 gap-5">
              <div 
                onClick={() => !isProcessing && bottle.status !== 'done' && triggerPicker(bottle.id, 'front')}
                className={`aspect-[3/4.2] rounded-3xl border-2 border-dashed flex flex-col items-center justify-center transition-all overflow-hidden relative group ${bottle.front ? 'border-amber-500/50 bg-black/40' : 'border-white/10'}`}
              >
                {bottle.front ? <img src={bottle.front} className="w-full h-full object-contain p-2" alt="" /> : (
                  <div className="text-center p-4">
                    <svg className="mx-auto mb-3 text-amber-600/40" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    <span className="text-[9px] text-slate-500 font-black uppercase tracking-widest block leading-tight">Fronte</span>
                  </div>
                )}
              </div>
              <div 
                onClick={() => !isProcessing && bottle.status !== 'done' && triggerPicker(bottle.id, 'back')}
                className={`aspect-[3/4.2] rounded-3xl border-2 border-dashed flex flex-col items-center justify-center transition-all overflow-hidden ${bottle.back ? 'border-amber-500/50 bg-black/40' : 'border-white/10'}`}
              >
                {bottle.back ? <img src={bottle.back} className="w-full h-full object-contain p-2" alt="" /> : (
                  <div className="text-center p-4">
                    <svg className="mx-auto mb-3 text-slate-700" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="9" y1="9" x2="15" y2="9"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/></svg>
                    <span className="text-[9px] text-slate-500 font-black uppercase tracking-widest block leading-tight">Retro</span>
                  </div>
                )}
              </div>
            </div>

            {bottle.status === 'loading' && (
              <div className="absolute inset-0 bg-black/90 backdrop-blur-md flex flex-col items-center justify-center text-amber-500 z-10 p-6 rounded-[2.5rem]">
                <div className="w-10 h-10 border-3 border-amber-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                <span className="text-[10px] font-black uppercase tracking-[0.3em] animate-pulse text-center">Analisi IA in corso...</span>
              </div>
            )}
            
            {bottle.status === 'error' && (
              <div className="absolute inset-0 bg-red-950/95 backdrop-blur-md flex flex-col items-center justify-center z-20 p-8 text-center rounded-[2.5rem]">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center text-red-500 mb-4">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                </div>
                <div className="max-h-32 overflow-y-auto mb-6">
                   <p className="text-[10px] text-red-200 font-mono break-all leading-tight">
                    {bottle.error}
                  </p>
                </div>
                <button 
                  onClick={() => setPendingBottles(prev => prev.map(p => p.id === bottle.id ? {...p, status: 'pending', error: undefined} : p))} 
                  className="w-full bg-red-600 text-white font-black py-4 rounded-2xl uppercase tracking-[0.2em] text-[10px] active:scale-95 transition-transform shadow-lg"
                >
                  Riprova
                </button>
              </div>
            )}

            {bottle.status === 'done' && (
              <div className="absolute inset-0 bg-green-950/80 backdrop-blur-sm flex flex-col items-center justify-center z-10 rounded-[2.5rem]">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center text-green-500">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-green-500 mt-4">Analisi Completata</span>
              </div>
            )}
          </div>
        ))}

        <button 
          onClick={() => setPendingBottles(prev => [...prev, { id: Math.random().toString(36).substr(2, 9), front: null, back: null, status: 'pending' }])}
          className="w-full py-10 border-2 border-dashed border-white/10 rounded-[2.5rem] text-slate-500 text-[10px] font-black uppercase tracking-widest hover:border-amber-600/20 hover:text-amber-600/40 active:scale-[0.98] transition-all"
          disabled={isProcessing}
        >
          + Aggiungi un'altra bottiglia
        </button>

        {/* Spazio finale per evitare che il footer copra l'ultimo elemento */}
        <div className="h-32"></div>
      </div>

      {/* Footer Fisso con Safe Area */}
      <div className="p-6 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-6 bg-black/80 backdrop-blur-3xl border-t border-white/10 z-[210]">
        <button
          onClick={processAll}
          disabled={isProcessing || !pendingBottles.some(b => (b.front || b.back) && b.status !== 'done')}
          className="w-full bg-amber-600 hover:bg-amber-500 disabled:opacity-20 text-white font-black py-6 rounded-3xl transition-all shadow-[0_15px_40px_-10px_rgba(217,119,6,0.5)] uppercase tracking-[0.3em] text-[12px] active:scale-[0.96]"
        >
          {isProcessing ? (
            <div className="flex items-center justify-center gap-3">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              <span>Analisi in corso...</span>
            </div>
          ) : "Analizza e Salva"}
        </button>
      </div>

      <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
    </div>
  );
};

export default AddWineModal;
